import { useNavigate, Link } from 'react-router-dom';
import { useTenant } from '@/app/providers/TenantProvider';
import { useAuth } from '@/app/providers/AuthProvider';
import {
  useDashboardMetrics,
  useDashboardActivity,
  useWhatsAppDeliveryRate,
  formatChange,
} from '../hooks/useDashboardMetrics';
import { useUpcomingEventCount } from '@/features/events/hooks/useEvents';
import {
  Users,
  UserCheck,
  Calendar,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  Plus,
  Sparkles,
  ChevronRight,
  Send,
  Mic,
  Minus,
} from 'lucide-react';

/* ── Skeleton de métrica ─────────────────────────────────────────────────── */
function MetricSkeleton() {
  return (
    <div className="relative overflow-hidden rounded-2xl card-premium p-6 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="h-3 w-28 rounded bg-midnight-4" />
        <div className="h-9 w-9 rounded-xl bg-midnight-4" />
      </div>
      <div className="mt-5 space-y-2">
        <div className="h-10 w-24 rounded bg-midnight-4" />
        <div className="h-4 w-20 rounded bg-midnight-4" />
      </div>
    </div>
  );
}

/* ── Card de métrica ─────────────────────────────────────────────────────── */
interface MetricCardProps {
  title: string;
  value: string | number;
  change: string;
  positive: boolean;
  icon: React.ElementType;
  accent: string;
  iconColor: string;
  subtitle?: string;
  isPlaceholder?: boolean;
}

function MetricCard({
  title, value, change, positive, icon: Icon,
  accent, iconColor, subtitle, isPlaceholder,
}: MetricCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-2xl card-premium p-6 transition-all duration-base hover:-translate-y-1 hover:shadow-deep cursor-pointer">
      <div
        className={`pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-gradient-to-br ${accent} blur-2xl opacity-60 group-hover:opacity-100 transition-opacity`}
      />
      <div className="relative flex items-start justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">
          {title}
        </span>
        <span className={`rounded-xl bg-midnight-2 hairline p-2.5 ${iconColor}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>

      <div className="relative mt-5 space-y-1.5">
        <span className="font-display text-3xl xl:text-4xl font-extrabold tracking-tight text-white">
          {isPlaceholder ? '—' : value.toLocaleString('pt-BR')}
        </span>
        {isPlaceholder ? (
          <p className="text-[10px] text-ink-faint">Em breve</p>
        ) : (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                positive
                  ? 'bg-emerald-500/15 text-emerald-300'
                  : 'bg-danger/15 text-danger'
              }`}
            >
              {positive
                ? <TrendingUp className="h-2.5 w-2.5" />
                : change === '—'
                ? <Minus className="h-2.5 w-2.5" />
                : <TrendingDown className="h-2.5 w-2.5" />
              }
              {change}
            </span>
            {subtitle && (
              <span className="text-[10px] text-ink-subtle">{subtitle}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Skeleton de atividade ───────────────────────────────────────────────── */
function ActivitySkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-start gap-4 rounded-xl p-3.5">
          <div className="h-10 w-10 shrink-0 rounded-xl bg-midnight-4" />
          <div className="flex-grow space-y-2 pt-1">
            <div className="h-2.5 w-20 rounded bg-midnight-4" />
            <div className="h-3.5 w-3/4 rounded bg-midnight-4" />
            <div className="h-2.5 w-16 rounded bg-midnight-4" />
          </div>
        </div>
      ))}
    </div>
  );
}

const COPILOT_SUGGESTIONS = [
  'Qual bairro registrou maior crescimento de apoiadores?',
  'Esboce um convite para a plenária de sábado.',
  'Liste os 10 líderes com maior cobertura territorial.',
];

/* ── Componente principal ────────────────────────────────────────────────── */
export function DashboardHome() {
  const { branding } = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics();
  const { data: activities, isLoading: activityLoading } = useDashboardActivity();
  const { data: upcomingEventCount = 0 } = useUpcomingEventCount();
  const { data: deliveryData } = useWhatsAppDeliveryRate();

  const firstName = (user?.email?.split('@')[0] || 'Gestor')
    .replace(/[._]/g, ' ')
    .split(' ')[0];

  // Variações calculadas
  const contactsChange = metrics
    ? formatChange(metrics.totalContacts, metrics.totalContactsLastMonth)
    : { label: '—', positive: true };

  const leadersChange = metrics
    ? formatChange(metrics.activeLeaders, metrics.activeLeadersLastMonth)
    : { label: '—', positive: true };

  const deliveryChange = deliveryData?.hasData
    ? formatChange(deliveryData.rate, deliveryData.ratePrevPeriod)
    : { label: '—', positive: true };

  return (
    <div className="space-y-10 pb-12 animate-fade-in">

      {/* ── Hero ── */}
      <section className="relative overflow-hidden rounded-3xl glass-strong shadow-deep">
        <div className="absolute inset-0 -z-10 grid-glow opacity-40" />
        <div
          className="absolute -top-32 -right-32 h-96 w-96 rounded-full blur-3xl pointer-events-none"
          style={{ background: 'color-mix(in oklab, var(--color-accent), transparent 75%)' }}
        />
        <div
          className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full blur-3xl pointer-events-none"
          style={{ background: 'color-mix(in oklab, var(--color-primary), transparent 60%)' }}
        />

        <div className="relative z-10 flex flex-col gap-8 p-8 md:flex-row md:items-center md:justify-between md:p-12">
          <div className="space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">
              <Sparkles className="h-3 w-3 animate-pulse" />
              <span>Painel Geral · Tempo real</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl xl:text-5xl font-extrabold tracking-tight">
              <span className="text-white">Olá, </span>
              <span className="text-gradient-cinematic capitalize">{firstName}</span>
              <span className="text-white">.</span>
            </h1>
            <p className="max-w-xl text-sm md:text-base leading-relaxed text-ink-muted">
              Bem-vindo ao centro de comando de{' '}
              <span className="font-semibold text-accent">
                {branding.candidate_name || branding.app_name}
              </span>
              . Monitore a campanha e coordene lideranças em tempo real.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/app/campanhas')}
              className="flex items-center gap-2 rounded-xl btn-accent px-5 py-3 text-sm font-semibold"
            >
              <Send className="h-4 w-4" />
              <span>Nova Campanha</span>
            </button>
            <button
              onClick={() => navigate('/app/contatos')}
              className="flex items-center gap-2 rounded-xl btn-ghost px-5 py-3 text-sm font-semibold"
            >
              <Plus className="h-4 w-4" />
              <span>Adicionar Contato</span>
            </button>
            <button
              onClick={() => navigate('/app/copiloto')}
              className="flex items-center gap-2 rounded-xl btn-ghost px-5 py-3 text-sm font-semibold"
            >
              <Sparkles className="h-4 w-4" />
              <span>Falar com Lumen</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Métricas ── */}
      <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {metricsLoading ? (
          Array.from({ length: 4 }).map((_, i) => <MetricSkeleton key={i} />)
        ) : (
          <>
            <MetricCard
              title="Total de Contatos"
              value={metrics?.totalContacts ?? 0}
              change={contactsChange.label}
              positive={contactsChange.positive}
              icon={Users}
              accent="from-blue-500/30 to-indigo-500/10"
              iconColor="text-blue-300"
              subtitle="vs mês passado"
            />
            <MetricCard
              title="Lideranças Ativas"
              value={metrics?.activeLeaders ?? 0}
              change={leadersChange.label}
              positive={leadersChange.positive}
              icon={UserCheck}
              accent="from-emerald-500/30 to-teal-500/10"
              iconColor="text-emerald-300"
              subtitle="vs mês passado"
            />
            <MetricCard
              title="Eventos na Agenda"
              value={upcomingEventCount}
              change="próximos"
              positive={true}
              icon={Calendar}
              accent="from-amber-500/30 to-orange-500/10"
              iconColor="text-amber-300"
              subtitle="eventos futuros"
            />
            <MetricCard
              title="Taxa de Entrega Zap"
              value={deliveryData?.hasData ? `${deliveryData.rate}%` : 0}
              change={deliveryChange.label}
              positive={deliveryChange.positive}
              icon={MessageSquare}
              accent="from-fuchsia-500/30 to-pink-500/10"
              iconColor="text-fuchsia-300"
              subtitle="vs 30 dias ant."
              isPlaceholder={!deliveryData?.hasData}
            />
          </>
        )}
      </section>

      {/* ── Atividades + Lumen ── */}
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">

        {/* Timeline de atividades */}
        <div className="lg:col-span-2 space-y-5 rounded-2xl card-premium p-6 md:p-7">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-bold text-white">
                Atividade recente
              </h2>
              <p className="text-xs text-ink-subtle mt-0.5">
                Últimos contatos cadastrados no sistema.
              </p>
            </div>
            <Link
              to="/app/contatos"
              className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
            >
              <span>Ver todos</span>
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>

          {activityLoading ? (
            <ActivitySkeleton />
          ) : !activities || activities.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <div className="h-12 w-12 rounded-2xl bg-midnight-4 flex items-center justify-center">
                <Users className="h-5 w-5 text-ink-subtle" />
              </div>
              <p className="text-sm text-ink-muted">Nenhuma atividade ainda.</p>
              <button
                onClick={() => navigate('/app/contatos')}
                className="text-xs font-semibold text-accent hover:underline"
              >
                Cadastrar primeiro contato →
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {activities.map((act) => (
                <Link
                  key={act.id}
                  to={`/app/contatos`}
                  className="group flex items-start gap-4 rounded-xl p-3.5 transition-all hover:bg-white/[0.04] hover:hairline"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 hairline">
                    <UserCheck className="h-4 w-4 text-accent" />
                  </div>
                  <div className="flex-grow space-y-0.5">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
                      Contato
                    </p>
                    <p className="text-sm font-medium text-ink">{act.text}</p>
                    <span className="text-[11px] text-ink-subtle">{act.time}</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-ink-subtle opacity-0 transition-all group-hover:opacity-100 group-hover:text-accent mt-1 shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Card Lumen IA — fundo navy via inline style; force-dark garante
            text-accent=cyan e text-white=branco sobre o fundo escuro em qualquer tema. */}
        <div className="force-dark relative flex min-h-[380px] flex-col justify-between overflow-hidden rounded-2xl p-6 md:p-7 shadow-deep">
          <div
            className="absolute inset-0 -z-10"
            style={{
              background:
                'linear-gradient(135deg, color-mix(in oklab, var(--color-primary), black 30%), color-mix(in oklab, var(--color-primary), transparent 30%))',
            }}
          />
          <div className="absolute inset-0 -z-10 grid-glow opacity-25" />
          <div
            className="absolute -top-20 -left-20 h-64 w-64 rounded-full blur-3xl pointer-events-none"
            style={{ background: 'color-mix(in oklab, var(--color-accent), transparent 70%)' }}
          />

          <div className="relative space-y-4">
            <span className="inline-flex items-center gap-1.5 rounded-full glass px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-accent">
              <Sparkles className="h-3 w-3" />
              <span>Lumen · Gemini</span>
            </span>
            <h3 className="font-display text-2xl font-bold tracking-tight text-white">
              Pergunte à sua base de dados.
            </h3>
            <p className="text-xs text-ink-muted leading-relaxed">
              Consulte cobertura, sentimento e redes de liderança, ou peça à IA para esboçar comunicações com seus dados reais.
            </p>
          </div>

          <div className="relative space-y-2 pt-6">
            {COPILOT_SUGGESTIONS.map((q, i) => (
              <Link
                key={i}
                to={`/app/copiloto`}
                className="group flex w-full items-center justify-between gap-2 rounded-xl glass px-3.5 py-2.5 text-left text-[11px] text-ink-muted transition-all hover:border-hairline-strong hover:text-ink hover:shadow-glow-xs cursor-pointer"
              >
                <span className="truncate">"{q}"</span>
                <ChevronRight className="h-3 w-3 shrink-0 text-ink-subtle transition-transform group-hover:translate-x-0.5 group-hover:text-accent" />
              </Link>
            ))}

            <div className="flex gap-2 pt-3">
              <button
                onClick={() => navigate('/app/copiloto')}
                className="flex-grow flex items-center justify-center gap-2 rounded-xl btn-accent py-3 text-xs font-semibold"
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span>Falar com Lumen</span>
              </button>
              <button className="flex items-center justify-center rounded-xl glass px-3 py-3 text-ink hover:text-accent transition-colors cursor-pointer" title="Entrada de voz (em breve)">
                <Mic className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
